<template>
  <div class="materials-quotes-page">
    <!-- Page Header -->
    <div class="page-header mb-4">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h3 class="mb-1">Materials Quotes</h3>
          <p class="text-muted mb-0">Manage materials quotes and follow-ups</p>
        </div>
        <button
          class="btn btn-primary"
          @click="showCreateForm = true"
        >
          <i class="fas fa-plus me-2"></i>
          New Materials Quote
        </button>
      </div>
    </div>

    <!-- Statistics Section -->
    <div class="statistics-section mb-5">
      <div v-if="loadingStats" class="text-center py-4">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
        <p class="text-muted mt-2">Loading statistics...</p>
      </div>
      <MaterialsQuoteStatistics 
        v-else
        :stats="quoteStats"
      />
    </div>

    <!-- Filters and Search -->
    <div class="filters-section mb-4">
      <div class="card">
        <div class="card-body">
          <div class="row">
            <div class="col-md-3">
              <div class="form-group">
                <label for="search">Search</label>
                <input
                  id="search"
                  v-model="filters.search"
                  type="text"
                  class="form-control"
                  placeholder="Search quotes..."
                  @input="debouncedSearch"
                />
              </div>
            </div>
            <div class="col-md-2">
              <div class="form-group">
                <label for="status">Status</label>
                <select id="status" v-model="filters.status" class="form-control" @change="loadQuotes">
                  <option value="">All Statuses</option>
                  <option value="DRAFT">Draft</option>
                  <option value="OPEN">Open</option>
                  <option value="SOLD">Sold</option>
                  <option value="REJECTED">Rejected</option>
                  <option value="EXPIRED">Expired</option>
                </select>
              </div>
            </div>
            <div class="col-md-2">
              <div class="form-group">
                <label for="quoteStatus">Follow-up Status</label>
                <select id="quoteStatus" v-model="filters.quoteStatus" class="form-control" @change="loadQuotes">
                  <option value="">All Follow-up</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="NO_FOLLOW_UP">No Follow-up</option>
                  <option value="CONVERTED">Converted</option>
                  <option value="LOST">Lost</option>
                </select>
              </div>
            </div>
            <div class="col-md-2">
              <div class="form-group">
                <label for="sortBy">Sort By</label>
                <select id="sortBy" v-model="filters.sortBy" class="form-control" @change="loadQuotes">
                  <option value="createdAt">Created Date</option>
                  <option value="title">Title</option>
                  <option value="customer.name">Customer</option>
                </select>
              </div>
            </div>
            <div class="col-md-2">
              <div class="form-group">
                <label for="sortOrder">Order</label>
                <select id="sortOrder" v-model="filters.sortOrder" class="form-control" @change="loadQuotes">
                  <option value="desc">Newest First</option>
                  <option value="asc">Oldest First</option>
                </select>
              </div>
            </div>
            <div class="col-md-1">
              <div class="form-group">
                <label>&nbsp;</label>
                <button class="btn btn-outline-secondary d-block" @click="clearFilters">
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Quotes Table -->
    <div class="quotes-section">
      <div class="card">
        <div class="card-body">
          <!-- Loading State -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Loading...</span>
            </div>
            <p class="mt-3 text-muted">Loading materials quotes...</p>
          </div>

          <!-- No Results -->
          <div v-else-if="quotes.length === 0" class="text-center py-5">
            <i class="fas fa-box fa-3x text-muted mb-3"></i>
            <h5 class="text-muted">No materials quotes found</h5>
            <p class="text-muted">
              {{ hasFilters ? 'Try adjusting your filters' : 'Create your first materials quote to get started' }}
            </p>
            <button
              v-if="!hasFilters"
              class="btn btn-primary"
              @click="showCreateForm = true"
            >
              <i class="fas fa-plus me-2"></i>
              Create Materials Quote
            </button>
          </div>

          <!-- Quotes Table -->
          <div v-else>
            <b-table
              :items="quotes"
              :fields="tableFields"
              striped
              hover
              responsive
              @row-clicked="viewQuote"
              tbody-tr-class="clickable-row"
            >
              <template #cell(shortId)="data">
                <strong class="text-primary">{{ data.item.shortId }}</strong>
              </template>

              <template #cell(title)="data">
                <div>
                  <strong>{{ data.item.title }}</strong>
                  <div v-if="data.item.description" class="text-muted small">
                    {{ data.item.description.substring(0, 50) }}{{ data.item.description.length > 50 ? '...' : '' }}
                  </div>
                </div>
              </template>

              <template #cell(amount)="data">
                <div class="text-right">
                  <strong v-if="data.item.amount" class="text-success">${{ formatCurrency(data.item.amount) }}</strong>
                  <span v-else class="text-muted">-</span>
                </div>
              </template>

              <template #cell(expirationDate)="data">
                <div v-if="data.item.expirationDate">
                  {{ formatDate(data.item.expirationDate) }}
                  <div v-if="isExpired(data.item.expirationDate)" class="text-danger small">
                    <i class="fas fa-exclamation-triangle"></i> Expired
                  </div>
                  <div v-else-if="isExpiringSoon(data.item.expirationDate)" class="text-warning small">
                    <i class="fas fa-clock"></i> Expires Soon
                  </div>
                </div>
                <span v-else class="text-muted">-</span>
              </template>

              <template #cell(customer)="data">
                <div v-if="data.item.customer && data.item.customer.name">
                  <strong>{{ data.item.customer.name }}</strong>
                  <div v-if="data.item.customer.company" class="text-muted small">
                    {{ data.item.customer.company }}
                  </div>
                  <div v-if="data.item.customer.email" class="text-muted small">
                    {{ data.item.customer.email }}
                  </div>
                </div>
                <span v-else class="text-muted">No customer</span>
              </template>

              <template #cell(status)="data">
                <b-badge :variant="getStatusVariant(data.item.status)">
                  {{ data.item.status }}
                </b-badge>
              </template>

              <template #cell(quoteStatus)="data">
                <b-badge :variant="getQuoteStatusVariant(data.item.quoteStatus)">
                  {{ getQuoteStatusText(data.item.quoteStatus) }}
                </b-badge>
              </template>


              <template #cell(createdAt)="data">
                <div>
                  {{ formatDate(data.item.createdAt) }}
                  <div class="text-muted small">
                    by {{ getUserDisplayName(data.item.createdBy) }}
                  </div>
                </div>
              </template>

              <template #cell(actions)="data">
                <div class="btn-group btn-group-sm">
                  <button
                    class="btn btn-outline-primary"
                    @click.stop="editQuote(data.item)"
                  >
                    <i class="fas fa-edit"></i>
                  </button>
                  <button
                    class="btn btn-outline-info"
                    @click.stop="duplicateQuote(data.item)"
                  >
                    <i class="fas fa-copy"></i>
                  </button>
                  <button
                    class="btn btn-outline-danger"
                    @click.stop="deleteQuote(data.item)"
                  >
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </template>
            </b-table>

            <!-- Pagination -->
            <div v-if="pagination.totalPages > 1" class="pagination-section">
              <b-pagination
                v-model="pagination.currentPage"
                :total-rows="pagination.totalCount"
                :per-page="pagination.limit"
                :limit="5"
                align="center"
                @change="onPageChange"
                class="mt-3"
              />
              <div class="pagination-info text-center">
                Showing {{ ((pagination.currentPage - 1) * pagination.limit) + 1 }} to 
                {{ Math.min(pagination.currentPage * pagination.limit, pagination.totalCount) }} of 
                {{ pagination.totalCount }} quotes
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <b-modal
      v-model="showCreateForm"
      :title="editingQuote ? 'Edit Materials Quote' : 'Create Materials Quote'"
      size="xl"
      hide-footer
      @hidden="resetForm"
    >
      <MaterialsQuoteForm
        :quote="editingQuote"
        :is-editing="!!editingQuote"
        :prefilled-customer="prefilledCustomerData"
        @saved="handleQuoteSaved"
        @cancel="showCreateForm = false"
      />
    </b-modal>

    <!-- View Modal -->
    <b-modal
      v-model="showViewModal"
      :title="`Materials Quote: ${viewingQuote && viewingQuote.shortId}`"
      size="xl"
      hide-footer
    >
      <div v-if="viewingQuote" class="quote-view">
        <!-- Quote Header with Customer Info -->
        <div class="customer-info-section mb-4">
          <div class="customer-header">
            <h6 class="customer-title">
              <i class="fas fa-user-circle me-2 text-primary"></i>
              Customer Information
            </h6>
          </div>
          
          <!-- Customer Details -->
          <div v-if="viewingQuote.customer && viewingQuote.customer.name" class="customer-details">
            <div class="customer-card">
              <div class="customer-main-info">
                <div class="customer-name">
                  <strong>{{ viewingQuote.customer.name }}</strong>
                </div>
                <div v-if="viewingQuote.customer.company" class="customer-company text-muted">
                  {{ viewingQuote.customer.company }}
                </div>
              </div>
              <div class="customer-contact-info">
                <div v-if="viewingQuote.customer.email" class="contact-item">
                  <i class="fas fa-envelope text-muted me-2"></i>
                  <span>{{ viewingQuote.customer.email }}</span>
                </div>
                <div v-if="viewingQuote.customer.phone" class="contact-item">
                  <i class="fas fa-phone text-muted me-2"></i>
                  <span>{{ viewingQuote.customer.phone }}</span>
                </div>
              </div>
            </div>
          </div>
          
          <!-- No Customer State -->
          <div v-else class="no-customer-state">
            <div class="text-center py-3">
              <i class="fas fa-user-slash fa-2x text-muted mb-2"></i>
              <h6 class="text-muted">No Customer</h6>
              <p class="text-muted mb-0">This quote was created without customer information</p>
            </div>
          </div>
        </div>

        <!-- Quote Details Section -->
        <div class="quote-details-section mb-4">
          <div class="quote-header-info">
            <!-- Title and Status Row -->
            <div class="row align-items-start mb-2">
              <div class="col-md-9">
                <h4 class="quote-title mb-1">{{ viewingQuote.title }}</h4>
                <p v-if="viewingQuote.description" class="quote-description text-muted mb-0">{{ viewingQuote.description }}</p>
              </div>
              <div class="col-md-3 text-end">
                <b-badge :variant="getStatusVariant(viewingQuote.status)" class="status-badge">
                  {{ viewingQuote.status }}
                </b-badge>
              </div>
            </div>
            
            <!-- Details Grid -->
            <div class="row details-grid">
              <div class="col-6 col-md-3">
                <div class="detail-item">
                  <span class="detail-label">Quote Type:</span>
                  <span class="detail-value">Materials Quote</span>
                </div>
              </div>
              <div class="col-6 col-md-3">
                <div class="detail-item">
                  <span class="detail-label">Quote Amount:</span>
                  <span class="detail-value">${{ formatCurrency(viewingQuote.amount || 0) }}</span>
                </div>
              </div>
              <div class="col-6 col-md-3">
                <div class="detail-item">
                  <span class="detail-label">Quote Date:</span>
                  <span class="detail-value">{{ formatDate(viewingQuote.createdAt) }}</span>
                </div>
              </div>
              <div class="col-6 col-md-3">
                <div class="detail-item">
                  <span class="detail-label">Created By:</span>
                  <span class="detail-value">{{ getUserDisplayName(viewingQuote.createdBy) }}</span>
                </div>
              </div>
            </div>
            
            <!-- Second Row for Last Updated if exists -->
            <div v-if="viewingQuote.updatedAt" class="row details-grid-secondary">
              <div class="col-6 col-md-3">
                <div class="detail-item">
                  <span class="detail-label">Last Updated:</span>
                  <span class="detail-value">{{ formatDate(viewingQuote.updatedAt) }}</span>
                </div>
              </div>
            </div>
            
            <!-- QuickBooks Numbers (if any) -->
            <div v-if="viewingQuote.quoteNumbers && viewingQuote.quoteNumbers.length > 0" class="quickbooks-section">
              <span class="detail-label">QuickBooks Quote Numbers:</span>
              <div class="quote-numbers-container">
                <span v-for="quoteNum in viewingQuote.quoteNumbers" :key="quoteNum" class="quote-number-badge">
                  {{ quoteNum }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Tabs for Follow-up and Details -->
        <b-tabs v-model="activeModalTab" class="quote-tabs">
          <!-- Next Follow-up Tab -->
          <b-tab title="📅 Next Follow-up">
            <div class="tab-content-padding">
              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Status</label>
                    <b-form-select
                      v-model="followUpForm.status"
                      :options="quoteStatusOptions"
                    />
                  </div>
                  
                  <div class="mb-3">
                    <label class="form-label">Priority</label>
                    <b-form-select
                      v-model="followUpForm.priority"
                      :options="priorityOptions"
                    />
                  </div>
                </div>
                
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Next Follow-up Date</label>
                    <div class="follow-up-date-controls mb-2">
                      <button 
                        type="button" 
                        class="btn btn-sm btn-outline-primary me-2"
                        @click="setFollowUpDate(7)"
                      >
                        1 Week
                      </button>
                      <button 
                        type="button" 
                        class="btn btn-sm btn-outline-primary me-2"
                        @click="setFollowUpDate(14)"
                      >
                        2 Weeks
                      </button>
                      <button 
                        type="button" 
                        class="btn btn-sm btn-outline-primary"
                        @click="setFollowUpDate(30)"
                      >
                        1 Month
                      </button>
                    </div>
                    <b-form-input
                      v-model="followUpForm.nextFollowUpDate"
                      type="datetime-local"
                    />
                  </div>
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label">Follow-up Notes</label>
                <b-form-textarea
                  v-model="followUpForm.note"
                  rows="4"
                  placeholder="Add notes about this follow-up..."
                />
              </div>
            </div>
          </b-tab>

          <!-- Past Follow-ups Tab -->
          <b-tab title="📋 Past Follow-ups">
            <div class="tab-content-padding">
              <FollowUpTimeline :follow-ups="viewingQuote.followUps" />
            </div>
          </b-tab>
        </b-tabs>

        <!-- Modal Actions -->
        <div class="modal-actions mt-4">
          <div class="d-flex justify-content-between">
            <div>
              <button class="btn btn-success me-2" @click="convertToMaterialsOrder(viewingQuote)">
                <i class="fas fa-exchange-alt me-1"></i> Convert to Materials Order
              </button>
            </div>
            <div>
              <button 
                v-if="activeModalTab === 0"
                class="btn btn-primary me-2" 
                @click="submitFollowUp"
                :disabled="submittingFollowUp"
              >
                <span v-if="submittingFollowUp">Saving...</span>
                <span v-else>Save Follow-up</span>
              </button>
              <button class="btn btn-outline-primary me-2" @click="editQuote(viewingQuote)">
                <i class="fas fa-edit me-1"></i> Edit
              </button>
              <button class="btn btn-outline-info me-2" @click="duplicateQuote(viewingQuote)">
                <i class="fas fa-copy me-1"></i> Duplicate
              </button>
              <button class="btn btn-outline-secondary" @click="showViewModal = false">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios'
import MaterialsQuoteForm from '@/components/MaterialsQuoteForm.vue'
import MaterialsQuoteStatistics from '@/components/MaterialsQuoteStatistics.vue'
import FollowUpTimeline from '@/components/FollowUpTimeline.vue'
import { debounce } from 'lodash'
import moment from 'moment'

export default {
  name: 'MaterialsQuotesPage',
  components: {
    MaterialsQuoteForm,
    MaterialsQuoteStatistics,
    FollowUpTimeline
  },
  data() {
    return {
      loading: false,
      loadingStats: false,
      quotes: [],
      quoteStats: {},
      showCreateForm: false,
      showViewModal: false,
      editingQuote: null,
      viewingQuote: null,
      prefilledCustomerData: null,
      activeModalTab: 0,
      submittingFollowUp: false,
      followUpForm: {
        status: 'IN_PROGRESS',
        priority: 'MEDIUM',
        nextFollowUpDate: '',
        note: ''
      },
      quoteStatusOptions: [
        { value: 'IN_PROGRESS', text: 'In Progress' },
        { value: 'NO_FOLLOW_UP', text: 'No Follow-up' },
        { value: 'CONVERTED', text: 'Converted' },
        { value: 'LOST', text: 'Lost' }
      ],
      priorityOptions: [
        { value: 'LOW', text: 'Low' },
        { value: 'MEDIUM', text: 'Medium' },
        { value: 'HIGH', text: 'High' },
        { value: 'URGENT', text: 'Urgent' }
      ],
      filters: {
        search: '',
        status: '',
        quoteStatus: '',
        sortBy: 'createdAt',
        sortOrder: 'desc'
      },
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        limit: 20,
        hasNext: false,
        hasPrev: false
      },
      tableFields: [
        { key: 'shortId', label: 'Quote #', sortable: true },
        { key: 'title', label: 'Title', sortable: true },
        { key: 'amount', label: 'Amount', sortable: true, class: 'text-right' },
        { key: 'expirationDate', label: 'Expires', sortable: true },
        { key: 'customer', label: 'Customer', sortable: false },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'quoteStatus', label: 'Follow-up', sortable: true },
        { key: 'createdAt', label: 'Created', sortable: true },
        { key: 'actions', label: 'Actions', class: 'text-center' }
      ]
    }
  },
  computed: {
    hasFilters() {
      return this.filters.search || this.filters.status || this.filters.quoteStatus
    }
  },
  async mounted() {
    await Promise.all([
      this.loadQuotes(),
      this.loadQuoteStats()
    ])

    // Check if we should create a new quote with customer data
    if (this.$route.params.createWithCustomer) {
      this.prefilledCustomerData = this.$route.params.createWithCustomer
      this.showCreateForm = true
    }

    // Check if we should edit a specific quote
    if (this.$route.params.editQuote) {
      const shortId = this.$route.params.editQuote
      try {
        const response = await axios.get(`/services/v2/materials-quotes/${shortId}`)
        this.editingQuote = response.data.quote
        this.showCreateForm = true
      } catch (error) {
        console.error('Error loading quote for editing:', error)
        this.$bvToast.toast('Error loading quote for editing', {
          title: 'Error',
          variant: 'danger'
        })
      }
    }
  },
  methods: {
    async loadQuotes() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.currentPage,
          limit: this.pagination.limit,
          sortBy: this.filters.sortBy,
          sortOrder: this.filters.sortOrder
        }

        if (this.filters.search) params.search = this.filters.search
        if (this.filters.status) params.status = this.filters.status
        if (this.filters.quoteStatus) params.quoteStatus = this.filters.quoteStatus

        const response = await axios.get('/services/v2/materials-quotes', { params })
        this.quotes = response.data.quotes
        this.pagination = response.data.pagination
      } catch (error) {
        console.error('Error loading materials quotes:', error)
        this.$bvToast.toast('Error loading materials quotes', {
          title: 'Error',
          variant: 'danger'
        })
      } finally {
        this.loading = false
      }
    },

    async loadQuoteStats() {
      this.loadingStats = true
      try {
        const response = await axios.get('/services/v2/materials-quotes/stats')
        this.quoteStats = response.data
      } catch (error) {
        console.error('Error loading materials quote statistics:', error)
        this.$bvToast.toast('Error loading materials quote statistics', {
          title: 'Error',
          variant: 'danger'
        })
      } finally {
        this.loadingStats = false
      }
    },

    debouncedSearch: debounce(function() {
      this.pagination.currentPage = 1
      this.loadQuotes()
    }, 500),

    clearFilters() {
      this.filters = {
        search: '',
        status: '',
        quoteStatus: '',
        sortBy: 'createdAt',
        sortOrder: 'desc'
      }
      this.pagination.currentPage = 1
      this.loadQuotes()
    },

    onPageChange(page) {
      this.pagination.currentPage = page
      this.loadQuotes()
    },

    editQuote(quote) {
      this.editingQuote = quote
      this.showViewModal = false
      this.showCreateForm = true
    },

    async duplicateQuote(quote) {
      try {
        const duplicatedQuote = {
          ...quote,
          title: `${quote.title} (Copy)`,
          status: 'DRAFT',
          shortId: undefined,
          _id: undefined,
          createdAt: undefined,
          updatedAt: undefined
        }

        const response = await axios.post('/services/v2/materials-quotes', duplicatedQuote)
        
        this.$bvToast.toast('Materials quote duplicated successfully', {
          title: 'Success',
          variant: 'success'
        })
        
        await this.loadQuotes()
        await this.loadQuoteStats()
      } catch (error) {
        console.error('Error duplicating quote:', error)
        this.$bvToast.toast('Error duplicating materials quote', {
          title: 'Error',
          variant: 'danger'
        })
      }
    },

    async deleteQuote(quote) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Are you sure you want to delete the materials quote "${quote.title}"? This action cannot be undone.`,
        {
          title: 'Confirm Deletion',
          size: 'md',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Delete',
          cancelTitle: 'Cancel',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true
        }
      )

      if (confirmed) {
        try {
          await axios.delete(`/services/v2/materials-quotes/${quote.shortId}`)
          
          this.$bvToast.toast('Materials quote deleted successfully', {
            title: 'Success',
            variant: 'success'
          })
          
          await this.loadQuotes()
          await this.loadQuoteStats()
        } catch (error) {
          console.error('Error deleting quote:', error)
          this.$bvToast.toast('Error deleting materials quote', {
            title: 'Error',
            variant: 'danger'
          })
        }
      }
    },

    handleQuoteSaved(quote) {
      this.showCreateForm = false
      this.loadQuotes()
      this.loadQuoteStats() // Refresh stats when quotes are updated
    },

    resetForm() {
      this.editingQuote = null
      this.prefilledCustomerData = null
    },

    getStatusVariant(status) {
      const variants = {
        'DRAFT': 'secondary',
        'OPEN': 'info',
        'SOLD': 'success',
        'REJECTED': 'danger',
        'EXPIRED': 'warning'
      }
      return variants[status] || 'secondary'
    },

    getQuoteStatusVariant(status) {
      const variants = {
        'IN_PROGRESS': 'primary',
        'NO_FOLLOW_UP': 'secondary',
        'CONVERTED': 'success',
        'LOST': 'danger'
      }
      return variants[status] || 'secondary'
    },

    getQuoteStatusText(status) {
      const texts = {
        'IN_PROGRESS': 'In Progress',
        'NO_FOLLOW_UP': 'No Follow-up',
        'CONVERTED': 'Converted',
        'LOST': 'Lost'
      }
      return texts[status] || status
    },


    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleDateString()
    },

    getUserDisplayName(email) {
      if (!email) return 'Unknown'
      const atIndex = email.indexOf('@')
      if (atIndex === -1) return email
      
      const namePart = email.substring(0, atIndex)
      return namePart.split('.').map(part => 
        part.charAt(0).toUpperCase() + part.slice(1)
      ).join(' ')
    },

    formatCurrency(amount) {
      if (!amount && amount !== 0) return '0.00'
      return parseFloat(amount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },

    isExpired(expirationDate) {
      if (!expirationDate) return false
      return moment(expirationDate).isBefore(moment(), 'day')
    },

    isExpiringSoon(expirationDate) {
      if (!expirationDate) return false
      const expDate = moment(expirationDate)
      const today = moment()
      const daysUntilExpiry = expDate.diff(today, 'days')
      return daysUntilExpiry >= 0 && daysUntilExpiry <= 7
    },

    // Follow-up related methods
    async viewQuote(quote) {
      // Fetch fresh quote data to ensure we have the latest followUps
      try {
        const response = await axios.get(`/services/v2/materials-quotes/${quote.shortId}`)
        this.viewingQuote = response.data.quote || quote
      } catch (error) {
        console.error('Error fetching quote details:', error)
        // Fallback to the passed quote if API fails
        this.viewingQuote = quote
      }
      
      // Initialize follow-up form
      this.followUpForm = {
        status: this.viewingQuote.quoteStatus || 'IN_PROGRESS',
        priority: this.viewingQuote.followUpPriority || 'MEDIUM',
        nextFollowUpDate: this.viewingQuote.nextFollowUpDate
          ? moment(this.viewingQuote.nextFollowUpDate).format('YYYY-MM-DDTHH:mm')
          : moment().add(7, 'days').format('YYYY-MM-DDTHH:mm'), // Default to 1 week
        note: ''
      }
      
      this.activeModalTab = 0
      this.showViewModal = true
    },

    setFollowUpDate(days) {
      const date = moment().add(days, 'days').format('YYYY-MM-DDTHH:mm')
      this.followUpForm.nextFollowUpDate = date
    },

    async submitFollowUp() {
      if (!this.viewingQuote || !this.followUpForm.note.trim()) {
        this.$bvToast.toast('Please add a note for this follow-up', {
          title: 'Validation Error',
          variant: 'warning'
        })
        return
      }

      this.submittingFollowUp = true
      
      try {
        const followUpData = {
          quoteStatus: this.followUpForm.status,
          followUpPriority: this.followUpForm.priority,
          nextFollowUpDate: this.followUpForm.nextFollowUpDate,
          note: this.followUpForm.note
        }

        const response = await axios.put(`/services/v2/materials-quotes/${this.viewingQuote.shortId}/follow-up`, followUpData)
        
        // Update the viewing quote with the latest data
        this.viewingQuote = response.data.quote
        
        // Reset the form
        this.followUpForm.note = ''
        
        this.$bvToast.toast('Follow-up saved successfully', {
          title: 'Success',
          variant: 'success'
        })
        
        // Refresh the quotes list
        await this.loadQuotes()
        
      } catch (error) {
        console.error('Error saving follow-up:', error)
        this.$bvToast.toast('Error saving follow-up', {
          title: 'Error',
          variant: 'danger'
        })
      } finally {
        this.submittingFollowUp = false
      }
    },

    convertToMaterialsOrder(quote) {
      // Close the current modal
      this.showViewModal = false
      
      // Prepare the order data based on the materials quote
      const orderData = {
        jobName: `Materials Order - ${quote.title}`,
        type: 'MATERIALS',
        deliveryMethod: 'PICKUP', // Default for materials orders
        productId: quote.shortId, // Store quote ID in productId field
        totalAmount: quote.amount || 0, // Carry over the quote amount
        customer: {
          name: quote.customer?.name || '',
          email: quote.customer?.email || '',
          phone: quote.customer?.phone || '',
          company: quote.customer?.company || '',
          vip: false
        },
        description: quote.description || '',
        materialsQuoteId: quote.shortId,
        materialsQuoteNumbers: quote.quoteNumbers || [],
        // Add any other relevant quote data
        notes: `Created from Materials Quote ${quote.shortId}${quote.description ? '\n\nOriginal Description:\n' + quote.description : ''}`
      }
      
      // Navigate to the create order page with pre-filled data
      this.$router.push({
        name: 'CreateOrderPage',
        params: {
          prefilledData: orderData
        }
      })
      
      // Show a success toast
      this.$bvToast.toast(`Navigating to create order page with data from Materials Quote ${quote.shortId}`, {
        title: 'Converting to Materials Order',
        variant: 'info',
        autoHideDelay: 3000
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.materials-quotes-page {
  .page-header {
    border-bottom: 1px solid #e9ecef;
    padding-bottom: 1rem;
  }

  .clickable-row {
    cursor: pointer;
    
    &:hover {
      background-color: #f8f9fa;
    }
  }

  .pagination-info {
    color: #6c757d;
    font-size: 0.875rem;
    margin-top: 0.5rem;
  }

  .quote-view {
    .customer-info-section {
      .customer-header {
        margin-bottom: 1rem;
        
        .customer-title {
          color: #495057;
          font-weight: 600;
          margin: 0;
        }
      }
      
      .customer-card {
        background: #f8f9fa;
        border: 1px solid #e9ecef;
        border-radius: 0.375rem;
        padding: 1rem;
        
        .customer-main-info {
          margin-bottom: 0.75rem;
          
          .customer-name {
            font-size: 1.1rem;
            color: #212529;
          }
          
          .customer-company {
            font-size: 0.9rem;
          }
        }
        
        .customer-contact-info {
          .contact-item {
            margin-bottom: 0.5rem;
            font-size: 0.9rem;
            
            &:last-child {
              margin-bottom: 0;
            }
          }
        }
      }
      
      .no-customer-state {
        background: #f8f9fa;
        border: 1px solid #e9ecef;
        border-radius: 0.375rem;
        padding: 1rem;
      }
    }
    
    .quote-details-section {
      .quote-header-info {
        background: #f8f9fa;
        border: 1px solid #e9ecef;
        border-radius: 0.375rem;
        padding: 1rem 1.25rem;
        
        .quote-title {
          color: #212529;
          font-weight: 600;
          font-size: 1.25rem;
        }
        
        .quote-description {
          font-size: 0.95rem;
          line-height: 1.4;
        }
        
        .status-badge {
          font-size: 0.85rem;
          padding: 0.4rem 0.8rem;
          font-weight: 500;
        }
        
        .details-grid {
          margin-top: 0.75rem;
          padding-top: 0.75rem;
          border-top: 1px solid #e9ecef;
        }
        
        .details-grid-secondary {
          margin-top: 0.5rem;
          padding-top: 0.5rem;
        }
        
        .detail-item {
          margin-bottom: 0.5rem;
          
          .detail-label {
            display: block;
            color: #6c757d;
            font-size: 0.8rem;
            font-weight: 500;
            margin-bottom: 0.1rem;
          }
          
          .detail-value {
            display: block;
            color: #212529;
            font-size: 0.9rem;
            font-weight: 400;
          }
        }
        
        .quickbooks-section {
          margin-top: 0.75rem;
          padding-top: 0.75rem;
          border-top: 1px solid #e9ecef;
          
          .detail-label {
            color: #6c757d;
            font-size: 0.8rem;
            font-weight: 500;
            margin-bottom: 0.5rem;
            display: block;
          }
          
          .quote-numbers-container {
            display: flex;
            flex-wrap: wrap;
            gap: 0.4rem;
          }
          
          .quote-number-badge {
            background: #e9ecef;
            border-radius: 0.25rem;
            padding: 0.2rem 0.5rem;
            font-family: monospace;
            font-size: 0.85rem;
            color: #495057;
            border: 1px solid #dee2e6;
          }
        }
      }
    }
    
    .quote-tabs {
      .tab-content-padding {
        padding: 1.5rem 0;
      }
      
      .follow-up-date-controls {
        .btn {
          font-size: 0.875rem;
        }
      }
    }
    
    .modal-actions {
      border-top: 1px solid #e9ecef;
      padding-top: 1rem;
    }
  }
}
</style>

